// StreamPlayerModal.js
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { BoxArrowUpRight, ClipboardCheck, EyeSlash } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import useCollections, { CollectionModal } from '../../hooks/useCollections';
import { openExternal, onActivate } from '../../utils/external';
import {
  governmentName,
  hostOf,
  liveStats,
  playerUrl,
  scopeLabel,
  streamCollectionItem,
  streamWatchUrl,
} from '../../utils/streams';

import '../feed/FeedItem.css';
import './Streams.css';

/**
 * StreamPlayerModal
 *
 * One stream, playing inside the app. Header, body and footer take the feed's
 * own modal classes (feed-modal-header / -body / -footer in FeedItem.css), so
 * the player looks like the feed's share modal in every theme.
 *
 * The iframe exists only while the modal is open: closing it unmounts the
 * player and stops the video. It uses the API's privacy-enhanced embed URL. A
 * stream whose owner refused embedding says so, and Watch on YouTube is always
 * there. The official page that links the channel is named under the player,
 * as the reason the channel is counted as a government's.
 *
 * Save sits in the footer beside Watch on YouTube, through the same collection
 * hook and modal the cards use and the same saved shape
 * (streamCollectionItem), so saving here marks the card as saved too. The
 * collection modal opens over the player, and the stream keeps playing
 * underneath it.
 *
 * The footer holds every action for a stream, since the compact live cards on
 * For You carry none of their own: Save, Share (the device's share sheet, or a
 * copied link where there is none), Watch on YouTube, and -- when the caller
 * passes onDismiss -- Hide, which takes the stream off For You for the session
 * and closes the player.
 */
function StreamPlayerModal({ stream, onHide, onDismiss = null }) {
  const { t } = useTranslation('common');

  // Called before the early return so the hook order never changes between an
  // open and a closed player.
  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    closeModal,
    handleSave,
    isStorageAvailable,
    CollectionButton,
  } = useCollections();

  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [stream?.video_id]);

  if (!stream) return null;

  const watchUrl = streamWatchUrl(stream);

  const share = async () => {
    if (!watchUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: stream.title, url: watchUrl });
      } catch {
        // Dismissed by the reader; nothing to do.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(watchUrl);
      setCopied(true);
    } catch {
      // No clipboard access; the YouTube button still has the link.
    }
  };

  const src = stream.embeddable ? playerUrl(stream.embed_url) : null;
  const page = stream.channel?.linked_from?.page || null;
  const pageHost = hostOf(page);

  const facts = [
    scopeLabel(stream.scope, t),
    governmentName(stream),
    stream.status === 'live' ? liveStats(stream, t) : null,
  ].filter(Boolean);

  return (
    <>
    <Modal show onHide={onHide} centered size="lg" className="stream-modal">
      <Modal.Header closeButton className="feed-modal-header">
        <Modal.Title className="stream-modal-title">{stream.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="feed-modal-body">
        {src ? (
          <div className="stream-player">
            <iframe
              src={src}
              title={stream.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        ) : (
          <p className="stream-modal-note">
            {t(
              'common:streamNotEmbeddable',
              'The channel does not allow this stream to be played outside YouTube.'
            )}
          </p>
        )}

        {facts.length > 0 && <p className="stream-modal-facts">{facts.join(' \u00b7 ')}</p>}

        {page && pageHost && (
          <p className="stream-modal-source">
            {t('common:streamLinkedFrom', 'Linked from the official site')}{' '}
            <span
              className="stream-modal-link"
              role="link"
              tabIndex={0}
              onClick={() => openExternal(page)}
              onKeyDown={onActivate(() => openExternal(page))}
            >
              {pageHost}
            </span>
          </p>
        )}
      </Modal.Body>
      <Modal.Footer className="feed-modal-footer stream-modal-footer">
        {/* .feed-item scopes the feed's button rules, so these are the same
            pills as on the card. */}
        <div className="feed-item stream-modal-actions">
          <CollectionButton item={streamCollectionItem(stream)} className="me-2" />
          {watchUrl && (
            <Button
              className="clickable me-2"
              id="shareButton"
              onClick={share}
              aria-label={t('common:share', 'Share')}
            >
              {copied ? (
                <ClipboardCheck size={16} aria-hidden />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z" /><path d="M6 12h16" /></svg>
              )}
            </Button>
          )}
          {watchUrl && (
            <Button className="clickable me-2" id="viewBillButton" onClick={() => openExternal(watchUrl)}>
              <BoxArrowUpRight size={12} className="me-2" aria-hidden />
              {t('common:watchOnYouTube', 'Watch on YouTube')}
            </Button>
          )}

        </div>
      </Modal.Footer>
    </Modal>

    <CollectionModal
      isModalOpen={isModalOpen}
      currentItem={currentItem}
      saveSuccess={saveSuccess}
      note={note}
      setNote={setNote}
      handleSave={handleSave}
      closeModal={closeModal}
      isStorageAvailable={isStorageAvailable}
    />
    </>
  );
}

export default StreamPlayerModal;